import type { DependencyList, EffectCallback } from 'react';
import { useEffect, useRef } from 'react';
import { GLOBAL_SERVICE_NAME } from './constants';
import { useWidgetAPI } from './context';
import { type EndpointsContainer } from './logic';
import { type Method, type SchemaData, type Service } from './types';
import { type Util, useUtil } from './util';

function useSafeEffect(callback: EffectCallback, deps: DependencyList) {
  const cleanupRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    const cleanup = callback();

    if (typeof cleanup === 'function') {
      cleanupRef.current = cleanup;
    }
  }, deps);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);
}

export const useRegisterAPIService = (
  serviceName: string,
  serviceProvider: () => Service,
  dependencyArray: unknown[]
) => {
  const { apiAccessor, apiManager } = useWidgetAPI();

  useSafeEffect(() => {
    const service = serviceProvider();

    service.on = (eventName, listener) => {
      return apiAccessor.on(eventName, listener, serviceName);
    };

    service.emit = (eventName, eventArgs) => {
      apiAccessor.emit(eventName, eventArgs, serviceName);
    };

    service.unsubscribeEvent = (eventName) => {
      apiAccessor.unsubscribeEvent(eventName, serviceName);
    };

    service.unsubscribeAllEvents = () => {
      apiAccessor.unsubscribeAllEvents(serviceName);
    };

    apiManager.registerService(serviceName, service);

    return () => {
      apiManager.unregisterEndpoint(serviceName);
    };
  }, dependencyArray);
};

export const useRegisterAPIMethod = (
  methodName: string,
  method: Method,
  dependencyArray: unknown[] = []
) => {
  const { apiManager } = useWidgetAPI();

  useSafeEffect(() => {
    apiManager.registerMethod(methodName, method);

    return () => apiManager.unregisterEndpoint(methodName);
  }, dependencyArray);
};

export const useEmitAPIEvent = () => {
  const { apiAccessor } = useWidgetAPI();

  return (
    eventName: string,
    eventArgs: unknown[] = [],
    serviceName = GLOBAL_SERVICE_NAME
  ) => apiAccessor.emit(eventName, eventArgs, serviceName);
};

export const useUnregisterAPIEndpoint = () => {
  const { apiManager } = useWidgetAPI();

  return (name: string) => apiManager.unregisterEndpoint(name);
};

export const useDefineAPISchema = (schemaData: SchemaData = {}) => {
  const { apiManager } = useWidgetAPI();

  apiManager.apiSchema.define(schemaData);
};

export function useCustomJS(customJS?: string, nonExecutionMode?: boolean) {
  const { apiAccessor } = useWidgetAPI();
  const util = useUtil();

  useEffect(() => {
    if (!customJS || nonExecutionMode) {
      return;
    }

    try {
      apiAccessor.unsubscribeAllEvents();
      const customJSFunction = new Function('widget', 'util', customJS) as (
        widget: EndpointsContainer,
        util: Util
      ) => void;
      customJSFunction(apiAccessor, util);
    } catch (e) {
      console.error(e);
    }
  }, [customJS, util]);
}
